import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import StudentSidebar from './StudentSidebar';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../utils/roleUtils';

const Header = ({ title, onNavigateToHome, onNavigateToCourses, onNavigateToProfile, onNavigateToPrompts, onNavigateToWritersRoom, onLogOut, onModeSwitch, showAdminMode = false }) => {
  const { userRole } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Refs for mobile menu focus management
  const hamburgerButtonRef = useRef(null);
  const mobileOverlayRef = useRef(null);

  // Move focus to the close button when mobile menu opens; return to hamburger on close
  useEffect(() => {
    if (isMobileMenuOpen && mobileOverlayRef.current) {
      const closeBtn = mobileOverlayRef.current.querySelector('button[aria-label="Close mobile menu"]');
      if (closeBtn) setTimeout(() => closeBtn.focus(), 0);
    } else if (!isMobileMenuOpen && hamburgerButtonRef.current) {
      hamburgerButtonRef.current.focus();
    }
  }, [isMobileMenuOpen]);

  // Focus trap: keep Tab / Shift+Tab inside the mobile overlay while open
  const handleOverlayKeyDown = useCallback((e) => {
    if (!mobileOverlayRef.current) return;
    if (e.key === 'Escape') {
      setIsMobileMenuOpen(false);
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = mobileOverlayRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, []);

  // Determine active route for mobile menu
  const isActive = (path) => {
    if (path === '/home') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    if (path === '/courses') {
      return location.pathname === '/courses' || location.pathname.startsWith('/courses/');
    }
    if (path === '/prompts') {
      return location.pathname === '/prompts' || location.pathname.startsWith('/prompts/');
    }
    if (path === '/writers-room') {
      return location.pathname === '/writers-room';
    }
    if (path === '/profile') {
      return location.pathname === '/profile';
    }
    return false;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogoClick = () => {
    if (onNavigateToHome) {
      onNavigateToHome();
    } else {
      onNavigateToCourses();
    }
  };

  return (
    <>
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden md:block">
        <StudentSidebar
          onNavigateToHome={onNavigateToHome}
          onNavigateToCourses={onNavigateToCourses}
          onNavigateToProfile={onNavigateToProfile}
          onNavigateToPrompts={onNavigateToPrompts}
          onNavigateToWritersRoom={onNavigateToWritersRoom}
          onLogOut={onLogOut}
          onModeSwitch={onModeSwitch}
          showAdminMode={showAdminMode}
        />
      </div>

      {/* Mobile Header Bar */}
      <div className="md:hidden relative w-full bg-[#282828]">
        <div className="relative pt-4 px-4 pb-4 flex justify-between items-center z-10">
          {/* STORYKIN Logo */}
          <button
            onClick={handleLogoClick}
            className="flex-shrink-0 hover:opacity-80 transition-opacity duration-200 cursor-pointer"
            aria-label="Go to Home"
          >
            <img 
              src="/images/storykin_logo.png" 
              alt="STORYKIN" 
              className="h-8 w-auto object-contain"
            />
          </button>

          {/* Mobile Hamburger Menu Button */}
          <button
            ref={hamburgerButtonRef}
            onClick={toggleMobileMenu}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-menu"
            aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
            className="flex items-center justify-center w-10 h-10 text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation Menu - Overlay */}
        {isMobileMenuOpen && (
          <div
            id="mobile-nav-menu"
            ref={mobileOverlayRef}
            onKeyDown={handleOverlayKeyDown}
            className="absolute top-0 left-0 right-0 z-20 bg-[#282828] min-h-screen"
          >
            {/* Mobile Menu Header */}
            <div className="flex justify-between items-center p-4 pt-16">
              <button
                onClick={handleLogoClick}
                className="hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                aria-label="Go to Home"
              >
                <img 
                  src="/images/storykin_logo.png" 
                  alt="STORYKIN" 
                  className="h-8 w-auto object-contain"
                />
              </button>
              <button
                onClick={toggleMobileMenu}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Close mobile menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Mobile Navigation Links */}
            <div className="px-4 py-8 space-y-6">
              {onNavigateToHome && (
                <button
                  onClick={() => {
                    onNavigateToHome();
                    setIsMobileMenuOpen(false);
                  }}
                  aria-current={isActive('/home') ? 'page' : undefined}
                  className={`block w-full text-left text-xl font-semibold transition-colors py-3 border-b ${
                    isActive('/home')
                      ? 'text-white border-white'
                      : 'text-white hover:text-gray-200 border-gray-700'
                  }`}
                >
                  Home
                </button>
              )}
              <button
                onClick={() => {
                  onNavigateToCourses();
                  setIsMobileMenuOpen(false);
                }}
                aria-current={isActive('/courses') ? 'page' : undefined}
                className={`block w-full text-left text-xl font-semibold transition-colors py-3 border-b ${
                  isActive('/courses')
                    ? 'text-white border-white'
                    : 'text-white hover:text-gray-200 border-gray-700'
                }`}
              >
                Courses
              </button>
              <button
                onClick={() => {
                  onNavigateToPrompts();
                  setIsMobileMenuOpen(false);
                }}
                aria-current={isActive('/prompts') ? 'page' : undefined}
                className={`block w-full text-left text-xl font-semibold transition-colors py-3 border-b ${
                  isActive('/prompts')
                    ? 'text-white border-white'
                    : 'text-white hover:text-gray-200 border-gray-700'
                }`}
              >
                Prompts
              </button>
              <button
                onClick={() => {
                  onNavigateToWritersRoom();
                  setIsMobileMenuOpen(false);
                }}
                aria-current={isActive('/writers-room') ? 'page' : undefined}
                className={`block w-full text-left text-xl font-semibold transition-colors py-3 border-b ${
                  isActive('/writers-room')
                    ? 'text-white border-white'
                    : 'text-white hover:text-gray-200 border-gray-700'
                }`}
              >
                Writers Room
              </button>
              {/* Mode Switch - only show for admins */}
              {isAdmin(userRole) && onModeSwitch && (
                <button
                  onClick={() => {
                    onModeSwitch();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-white text-xl font-semibold hover:text-gray-200 transition-colors py-3 border-b border-gray-700"
                >
                  {showAdminMode ? 'Student Mode' : 'Admin Mode'}
                </button>
              )}
              <button
                onClick={() => {
                  onNavigateToProfile();
                  setIsMobileMenuOpen(false);
                }}
                aria-current={isActive('/profile') ? 'page' : undefined}
                className={`block w-full text-left text-xl font-semibold transition-colors py-3 border-b ${
                  isActive('/profile')
                    ? 'text-white border-white'
                    : 'text-white hover:text-gray-200 border-gray-700'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => {
                  onLogOut();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left text-white text-xl font-semibold hover:text-gray-200 transition-colors py-3 border-b border-gray-700"
              >
                Log Out
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Header;